import React from 'react';
import style from './style.module.scss';
import CloseIcon from '../../icons/X.svg?react';
import { Store, StoresStore } from '../../stores/stores';
import { StoreBrand } from '../../stores/constants';
import { Moment } from 'moment';
import moment from 'moment';
import { Button } from '../button';
import { observer } from 'mobx-react';
import { DatePicker, Divider } from 'antd';
import { StoreEditingLocation } from '../store-editing-location';
import { StoreEditingDays } from '../store-editing-days';
import { StoreEditingServices } from '../store-editing-services';
import { StoreTag } from '../store-tag';
import { WEEKDAYS } from '../../helpers/store-helper';
import { StoreEditingCarWashStatus } from '../store-editing-car-wash-status';
import { SaveButton } from '../action-buttons';
import { NotificationManager } from '../notification-manager';
import { useCarWashStore } from '../../contexts/mobx';

type StoreEditingProps = {
    onCloseClick: () => void;
    storeObject: Store;
    store: StoresStore;
    onSave: () => void;
};

const DATE_FORMAT = 'YYYY-MM-DD';

type Weekday = typeof WEEKDAYS[number];
type HoursMap = { [key: string]: Weekday[] };

function groupDaysByHours(hours: (string | null | undefined)[]): HoursMap {
    const hoursMap: HoursMap = {};

    hours.forEach((hour, index) => {
        if (hour) {
            const day = WEEKDAYS[index];
            if (!hoursMap[hour]) {
                hoursMap[hour] = [];
            }
            hoursMap[hour].push(day);
        }
    });
    return hoursMap;
}

export const StoreEditing = observer(
    ({ onCloseClick, storeObject, store, onSave }: StoreEditingProps) => {
        const [selectedServices, setSelectedServices] = React.useState<
            string[]
        >([]);
        const [selectedWeekdays, setSelectedWeekdays] = React.useState<
            string[]
        >([]);
        const [storeStatus, setStoreStatus] = React.useState('');
        const [carWashStatus, setCarWashStatus] = React.useState('');
        const [carWashClosedOn, setCarWashClosedOn] = React.useState<
            string | null
        >(null);
        const carWashStore = useCarWashStore();
        const isCarWashStation = carWashStore.carwashStores.some(
            (station) => String(station.storeId) === String(storeObject.id)
        );
        const [storePhone, setStorePhone] = React.useState('');
        const [storeCoordinates, setStoreCoordinates] = React.useState({
            lat: storeObject?.lat ?? 0,
            lng: storeObject?.lng ?? 0,
        });

        const determineStoreStatus = (
            selectedWeekdays: string[],
            openHoursComponents: {
                id: number;
                startTime: Moment | null;
                endTime: Moment | null;
                open: boolean;
                selectedWeekdays: string[];
            }[]
        ) => {
            const allDaysSelected = WEEKDAYS.every((day) =>
                selectedWeekdays.includes(day)
            );
            const validOpenHoursComponents = openHoursComponents.filter(
                (component) => component.selectedWeekdays.length > 0
            );
            const allDays24Hours = validOpenHoursComponents.every(
                (component) =>
                    component.startTime &&
                    component.endTime &&
                    component.startTime.format('HH:mm') === '00:00' &&
                    component.endTime.format('HH:mm') === '00:00' &&
                    component.open
            );

            if (allDaysSelected && allDays24Hours) {
                setStoreStatus('open-24');
            } else if (
                selectedWeekdays.length > 0 &&
                openHoursComponents.some(
                    (component) =>
                        component.startTime &&
                        component.endTime &&
                        component.open
                )
            ) {
                setStoreStatus('open');
            } else {
                setStoreStatus('closed');
            }
        };

        const [openHoursComponents, setOpenHoursComponents] = React.useState<
            {
                id: number;
                startTime: Moment | null;
                endTime: Moment | null;
                open: boolean;
                selectedWeekdays: string[];
            }[]
        >([]);
        const [isLoading, setIsLoading] = React.useState(false);

        function initalState() {
            if (storeObject) {
                setCarWashStatus(storeObject.carWashStatus);
                setCarWashClosedOn(storeObject.carWashClosedOn ?? null);
                setSelectedServices(storeObject.services);
                setOpenHoursComponents([]);
                setSelectedWeekdays([]);
                setStorePhone(storeObject.tel ?? '');
                setStoreCoordinates({
                    lat: storeObject.lat ?? 0,
                    lng: storeObject.lng ?? 0,
                });
                let allSelectedWeekdays: string[] = [];
                const groupedDays = groupDaysByHours(storeObject.hours);
                for (const [hour, days] of Object.entries(groupedDays)) {
                    allSelectedWeekdays = [...allSelectedWeekdays, ...days];
                    setOpenHoursComponents((prevComponents) => [
                        ...prevComponents,
                        {
                            id: prevComponents.length,
                            startTime: moment(hour.substring(0, 5), 'HH:mm'),
                            endTime: moment(
                                hour.substring(hour.length - 5),
                                'HH:mm'
                            ),
                            open: true,
                            selectedWeekdays: days,
                        },
                    ]);
                    setSelectedWeekdays(allSelectedWeekdays);
                }
                determineStoreStatus(allSelectedWeekdays, openHoursComponents);
            }
        }

        React.useEffect(() => {
            initalState();
        }, [storeObject]);

        React.useEffect(() => {
            if (carWashStore.carwashStores.length === 0) {
                carWashStore.fetchCarWashStores();
            }
        }, [carWashStore]);

        React.useEffect(() => {
            determineStoreStatus(selectedWeekdays, openHoursComponents);
        }, [openHoursComponents, selectedWeekdays]);

        // A closing date only stands while the carwash is closed.
        const selectCarWashStatus = (status: string) => {
            setCarWashStatus(status);
            if (status !== 'closed') {
                setCarWashClosedOn(null);
            }
        };

        const saveChanges = async () => {
            setIsLoading(true);
            try {
                const hours = new Array(7).fill('');
                openHoursComponents.forEach((component) => {
                    if (
                        component.startTime &&
                        component.endTime &&
                        component.open
                    ) {
                        const formattedHour = `${component.startTime.format(
                            'HH:mm'
                        )}-${component.endTime.format('HH:mm')}`;
                        component.selectedWeekdays.forEach((weekday) => {
                            const index = WEEKDAYS.indexOf(weekday);
                            if (index !== -1) {
                                hours[index] = formattedHour;
                            }
                        });
                    }
                });

                const payload = {
                    brand: storeObject.brand as StoreBrand,
                    additions: [] as Store[],
                    modifications: [
                        {
                            id: storeObject.id,
                            nref: storeObject.nref,
                            title: storeObject.title,
                            lat: storeCoordinates.lat,
                            lng: storeCoordinates.lng,
                            img: storeObject.img,
                            tel: storePhone,
                            hours: hours,
                            services: selectedServices,
                            status: storeStatus,
                            carWashStatus: carWashStatus,
                            carWashClosedOn: carWashClosedOn,
                            brand: storeObject.brand,
                            addr: {
                                street: storeObject.addr.street,
                                city: storeObject.addr.city,
                                post: storeObject.addr.post,
                            },
                        },
                    ],
                };
                const result = await store.uploadChanges(payload);
                if (result === null) return;

                if (result.applied.modified + result.applied.added > 0) {
                    NotificationManager.showSuccess('Store updated!');
                    onSave();
                } else if (result.skipped.length > 0) {
                    const skipped = result.skipped[0];
                    NotificationManager.showError(
                        skipped.reason === 'brand-mismatch'
                            ? `Store belongs to ${
                                  skipped.existingBrand ?? 'another brand'
                              }`
                            : 'Store not found in the system'
                    );
                }
            } finally {
                setIsLoading(false);
            }
        };

        const resetChanges = () => {
            initalState();
        };

        return (
            <>
                <div className={style.container}>
                    <div className={style.content}>
                        <div className={style.close} onClick={onCloseClick}>
                            <CloseIcon />
                        </div>
                        <div className={style.editing_type}>
                            #{storeObject.nref}
                        </div>
                        <div className={style.title}>Store editing</div>
                        <div className={style.store_info}>
                            <StoreEditingLocation
                                storeObject={storeObject}
                                storePhone={storePhone}
                                setStorePhone={setStorePhone}
                                storeCoordinates={storeCoordinates}
                                setStoreCoordinates={setStoreCoordinates}
                            />
                            <Divider className={style.section_divider} />
                            <div className={style.single_info}>
                                <b>Store status: </b>
                                <div>
                                    <StoreTag status={storeStatus} />
                                </div>
                            </div>
                            <div className={style.single_info}>
                                <b>Opening hours:</b>
                                <StoreEditingDays
                                    openHoursComponents={openHoursComponents}
                                    setOpenHoursComponents={
                                        setOpenHoursComponents
                                    }
                                    selectedWeekdays={selectedWeekdays}
                                    setSelectedWeekdays={setSelectedWeekdays}
                                />
                            </div>
                            <Divider className={style.section_divider} />
                            <div className={style.single_info}>
                                <b>Car Wash Status: </b>
                                <div>
                                    {storeObject.brand ===
                                    StoreBrand.CIRCLE_K ? (
                                        <span
                                            className={style.not_available_hint}
                                        >
                                            Not available for Circle K
                                        </span>
                                    ) : (
                                        <StoreEditingCarWashStatus
                                            selectedStatus={carWashStatus}
                                            setSelectedStatus={
                                                selectCarWashStatus
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            {storeObject.brand !== StoreBrand.CIRCLE_K &&
                                carWashStatus === 'closed' &&
                                isCarWashStation && (
                                    <div className={style.single_info}>
                                        <b>Car wash closing date: </b>
                                        <div>
                                            <DatePicker
                                                format={DATE_FORMAT}
                                                value={
                                                    carWashClosedOn
                                                        ? moment(
                                                              carWashClosedOn,
                                                              DATE_FORMAT
                                                          )
                                                        : null
                                                }
                                                onChange={(date) =>
                                                    setCarWashClosedOn(
                                                        date
                                                            ? date.format(
                                                                  DATE_FORMAT
                                                              )
                                                            : null
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            <Divider className={style.section_divider} />
                            <div className={style.single_info}>
                                <b>Services:</b>
                                <StoreEditingServices
                                    selectedServices={selectedServices}
                                    setSelectedServices={setSelectedServices}
                                />
                            </div>
                            <div className="save_buttons">
                                <Button
                                    variant="secondary-destructive"
                                    onClick={resetChanges}
                                >
                                    Reset to initial
                                </Button>
                                <SaveButton
                                    onClick={saveChanges}
                                    loading={isLoading}
                                >
                                    Save changes
                                </SaveButton>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
);
